import { axios, form } from '@shared/api'
import { computed, ref } from 'vue'
import { fromJSONSchema } from '@bagelink/vue'
import type { FormDefinitionRead } from '@shared/types'

/**
 * Shared newsletter-subscribe popup state.
 *
 * The subscribe dialog is rendered once (by `BlockNav` via `NewsletterDialog`),
 * but any block — the nav "Newsletter" links, the footer "Subscribe" row — can
 * open it by calling `openNewsletter()`. State lives at module scope so every
 * caller drives the single shared dialog.
 */

type NewsletterState = 'form' | 'submitting' | 'success' | 'error'

const FORM_NAME = 'newsletter'

const isOpen = ref(false)
const formData = ref<Record<string, any>>({})
const state = ref<NewsletterState>('form')
const definition = ref<FormDefinitionRead | null>(null)
const loadError = ref('')
let loadPromise: Promise<void> | null = null

const schema = computed(() => {
	const raw = definition.value?.schema_def as Record<string, any> | undefined
	return raw ? fromJSONSchema(raw as any).class('grid gap-05') : null
})

function loadNewsletterForm() {
	if (loadPromise) return loadPromise
	loadPromise = axios
		.get(`/form/public/definitions/by-name/${FORM_NAME}`)
		.then((response: any) => {
			definition.value = (response?.data ?? response) as FormDefinitionRead
			loadError.value = ''
		})
		.catch(() => {
			definition.value = null
			loadError.value = 'The signup form is unavailable.'
			loadPromise = null
		})
	return loadPromise
}

function openNewsletter() {
	formData.value = {}
	state.value = 'form'
	isOpen.value = true
	void loadNewsletterForm()
}

async function submitNewsletter(payload = formData.value) {
	if (state.value === 'submitting') return
	state.value = 'submitting'
	try {
		await form.submit.postByFormName({
			formName: FORM_NAME,
			formSubmissionCreate: {
				_meta: {},
				...payload,
			},
		})
		state.value = 'success'
	}
	catch (error) {
		state.value = 'error'
		throw error
	}
}

export function useNewsletter() {
	return {
		isOpen,
		formData,
		state,
		schema,
		loadError,
		openNewsletter,
		loadNewsletterForm,
		submitNewsletter,
	}
}
